import React from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import format from 'date-fns/format';
import { useSelector } from 'react-redux';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';

// eslint-disable-next-line import/prefer-default-export
export const RenderDatePicker = ({ input, className, placeholder, popperPlacement, popperModifiers, isClearable, dateFormat, maxDate, minDate, id, allowFutureDate, showMonthYearPicker }) => {
  const getParsedMoment = React.useCallback(value => moment(value, dateFormat.toUpperCase(), true), [dateFormat]);

  const isStrictDateValid = React.useCallback((value) => {
    if (!value) {
      return false;
    }

    return getParsedMoment(value).isValid();
  }, [getParsedMoment]);

  const parseStrictDate = React.useCallback((value) => {
    if (!isStrictDateValid(value)) {
      return null;
    }

    return getParsedMoment(value).toDate();
  }, [getParsedMoment, isStrictDateValid]);

  const [startDate, setStartDate] = React.useState(() => parseStrictDate(input.value));
  function onHandleKeyDown(e) {
    if (e.keyCode === 8 || e.keyCode === 46) {
      setStartDate(null);
      input.onChange(null);
    }
  }

  const hasPermission = useSelector(
    state => AccessControl.hasPermission(state.auth.permissions, [ActivityPerms.scannedFormSignDateFreeTextInput])
  );

  const canAllowFutureDate = allowFutureDate && hasPermission;

  const hasInvalidInputValue = Boolean(input.value) && !isStrictDateValid(input.value);
  const applicablePlaceholder = hasInvalidInputValue ? input.value : placeholder;
  const inputClassName = `${ hasInvalidInputValue ? 'dark-placeholder-text ' : '' }${ className === '' ? 'form-select' : className }`;

  React.useEffect(() => {
    setStartDate(parseStrictDate(input.value));
  }, [input.value, parseStrictDate]);

  return (
    <DatePicker
      placeholder={ applicablePlaceholder }
      fixedHeight
      showMonthDropdown={ !showMonthYearPicker }
      showYearDropdown={ !showMonthYearPicker }
      showMonthYearPicker={ showMonthYearPicker }
      placeholderText={ applicablePlaceholder }
      maxDate={ canAllowFutureDate ? null : maxDate }
      minDate={ minDate }
      popperPlacement={ popperPlacement }
      popperModifiers={ popperModifiers ?? {
        preventOverflow: {
          enabled: true,
          escapeWithReference: true,
          boundariesElement: 'viewport'
        }
      } }
      autoComplete="off"
      isClearable={ isClearable }
      dropdownMode="select"
      className={ inputClassName }
      dateFormat={ dateFormat }
      selected={ startDate }
      onKeyDown={ e => onHandleKeyDown(e) }
      onChange={ (date, e) => { setStartDate(date); input.onChange(date ? format(date, dateFormat) : null); } }
      id={ id }
    />
  );
};

RenderDatePicker.propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  popperPlacement: PropTypes.string,
  popperModifiers: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  isClearable: PropTypes.bool,
  dateFormat: PropTypes.string,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  id: PropTypes.string,
  allowFutureDate: PropTypes.bool,
  showMonthYearPicker: PropTypes.bool,
};

RenderDatePicker.defaultProps = {
  placeholder: '',
  popperPlacement: 'bottom-start',
  popperModifiers: [],
  className: '',
  isClearable: false,
  dateFormat: 'MMM dd, yyyy',
  maxDate: null,
  minDate: null,
  id: null,
  allowFutureDate: false,
  showMonthYearPicker: false,
};
